import { useEffect, useState } from 'react';
import { Form } from 'antd';
import { useTranslation } from 'react-i18next';
import RenderModal from '../../../components/common/Modal/Modal';
import InputComponent from '../../../components/common/InputComponent/InputComponent';
import ButtonPrimaryLarge from '../../../components/common/ButtonPrimary/ButtonPrimaryLarge';
import {
  createCustomer,
  updateCustomer,
} from '../../../services/customers/customersApi';
import { openMessageSuccess } from '../../../components/common/Message/openMessageSuccess';
import { openMessageError } from '../../../components/common/Message/openMessageError';

const EMPTY = { name: '', phone: '', email: '', address: '', note: '' };

const CustomerFormFields = ({ form, customer }) => {
  const { t } = useTranslation();

  // Form mới mỗi lần mở: đổ lại dữ liệu khách (hoặc rỗng khi tạo mới), không giữ giá trị của lần mở trước
  useEffect(() => {
    form.resetFields();
    form.setFieldsValue({ ...EMPTY, ...customer });
  }, [form, customer]);

  return (
    <Form
      form={form}
      layout="vertical"
      className="flex flex-col items-start px-24 py-16 gap-16 grow self-stretch"
    >
      <Form.Item
        label={t("Customer's name")}
        name="name"
        className="self-stretch"
        rules={[
          {
            validator: (_, value) =>
              value?.trim()
                ? Promise.resolve()
                : Promise.reject(t('Enter name')),
          },
        ]}
      >
        <InputComponent size="large" placeholder={t("Customer's name")} />
      </Form.Item>
      <div className="grid grid-cols-2 gap-16 self-stretch">
        <Form.Item label={t('Phone number')} name="phone">
          <InputComponent size="large" placeholder={t('Phone number')} />
        </Form.Item>
        <Form.Item label={t('Email')} name="email">
          <InputComponent size="large" placeholder={t('Email')} />
        </Form.Item>
      </div>
      <Form.Item label={t('Address')} name="address" className="self-stretch">
        <InputComponent size="large" placeholder={t('Address')} />
      </Form.Item>
      <Form.Item label={t('Note')} name="note" className="self-stretch">
        <InputComponent size="large" placeholder={t('Note')} />
      </Form.Item>
    </Form>
  );
};

/** Tạo khách ảo (customer == null) hoặc sửa thông tin khách. Trạng thái chốt không sửa ở đây. */
const CustomerFormModal = ({ open, customer, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    setIsSaving(true);
    try {
      const saved = customer
        ? await updateCustomer(customer.id, values)
        : await createCustomer(values);
      openMessageSuccess({
        content: customer ? t('Customer updated') : t('Customer created'),
      });
      onSaved?.(saved);
      onClose();
    } catch (error) {
      openMessageError({
        content: error?.data?.message ?? t('Unable to save customer'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) return null;

  return (
    <RenderModal
      open={open}
      onClose={onClose}
      destroyOnHidden
      width={640}
      title={
        <span className="text-headline-small">
          {customer ? t('Edit customer') : t('Create customer')}
        </span>
      }
      classNames={{
        header:
          '!p-24 !border-b-[1px] !border-solid !border-outline-default-primary !mb-0',
        content: '!rounded-[16px]',
        body: '!p-0',
        footer:
          '!p-24 !border-t-[1px] !border-solid !border-outline-default-primary !mt-0',
      }}
      footer={[
        <ButtonPrimaryLarge
          key="save"
          title={t('Save changes')}
          loading={isSaving}
          disabled={isSaving}
          onClick={handleSave}
        />,
      ]}
      content={<CustomerFormFields form={form} customer={customer} />}
    />
  );
};

export default CustomerFormModal;
